// Page OCR — Upload facture → extraction Claude Vision → ingestion Achats/Compta
import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, FileText, Loader2, CheckCircle2, AlertCircle, ShoppingCart, BookOpen, Scan } from "lucide-react";
import { api, formatXAF } from "@/lib/api";
import { useEntity } from "@/lib/entity";

export default function OCRInvoice() {
  const { currentEntityId, entities } = useEntity();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [err, setErr] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [result, setResult] = useState(null);
  const [targetEntity, setTargetEntity] = useState(currentEntityId === "GROUPE" ? "ROPS" : currentEntityId);
  const fileRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setExtracted(null);
    setResult(null);
    setErr("");
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  async function extract() {
    if (!file) return;
    setExtracting(true);
    setErr("");
    setExtracted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/ocr/invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExtracted(data.extracted);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Erreur OCR");
    } finally {
      setExtracting(false);
    }
  }

  async function ingest(target) {
    setIngesting(true);
    setErr("");
    try {
      const { data } = await api.post("/ai/ocr/ingest", { target, entity_id: targetEntity, extracted });
      setResult({ target, data });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally {
      setIngesting(false);
    }
  }

  const opEntities = entities.filter((e) => e.id !== "GROUPE");

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 max-w-5xl" data-testid="ocr-page">
      <div>
        <div className="overline text-citron mb-2">IA Augmentée · Claude Vision</div>
        <h2 className="font-heading text-3xl font-bold tracking-tight">OCR Facture</h2>
        <p className="text-sm text-obsidian-400 mt-1">Scannez une facture fournisseur — l'IA extrait montants, lignes, TVA, NIF et injecte automatiquement dans Achats ou Comptabilité.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Scan size={16} className="text-citron" />
            <h3 className="font-heading font-bold">1. Charger la facture</h3>
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-line rounded-sm p-8 text-center cursor-pointer hover:border-citron/60 hover:bg-obsidian-900 transition-colors"
            data-testid="ocr-dropzone"
          >
            <Upload size={32} className="mx-auto text-obsidian-500 mb-3" />
            <p className="text-sm font-medium">Cliquez ou glissez votre facture ici</p>
            <p className="text-xs text-obsidian-500 mt-1">JPG, PNG, WebP, PDF · max 8 Mo</p>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onPick} className="hidden" data-testid="ocr-file-input" />
          </div>

          {file && (
            <div className="mt-4 p-3 bg-obsidian-900 rounded-sm flex items-center gap-3">
              <FileText size={16} className="text-citron flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{file.name}</div>
                <div className="text-[10px] text-obsidian-500 font-mono">{(file.size / 1024).toFixed(1)} KB · {file.type}</div>
              </div>
              <button onClick={extract} disabled={extracting} className="btn-primary h-9" data-testid="ocr-extract-button">
                {extracting ? <><Loader2 size={14} className="animate-spin" /> Analyse…</> : <><Scan size={14} /> Extraire</>}
              </button>
            </div>
          )}

          {preview && (
            <div className="mt-4 bg-obsidian-900 p-2 rounded-sm">
              <img src={preview} alt="aperçu" className="max-h-96 mx-auto rounded-sm" />
            </div>
          )}

          {err && (
            <div className="mt-3 flex items-start gap-2 p-3 bg-bad/10 border border-bad/30 rounded-sm text-bad text-xs">
              <AlertCircle size={14} className="mt-0.5" /> {err}
            </div>
          )}
        </div>

        {/* Résultat */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={16} className="text-citron" />
            <h3 className="font-heading font-bold">2. Données extraites</h3>
          </div>

          {!extracted && !extracting && (
            <div className="text-center py-12 text-sm text-obsidian-500">
              Aucune extraction. Chargez une facture et cliquez sur "Extraire".
            </div>
          )}

          {extracting && (
            <div className="text-center py-12">
              <Loader2 size={32} className="animate-spin mx-auto text-citron mb-3" />
              <div className="text-sm text-obsidian-400">Claude Vision analyse votre facture…</div>
            </div>
          )}

          {extracted && (
            <div className="space-y-3 text-sm">
              <Field label="Fournisseur" value={extracted.vendor_name} />
              <Field label="NIF fiscal" value={extracted.vendor_nif} mono />
              <Field label="N° facture" value={extracted.invoice_number} mono />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date" value={extracted.date} mono />
                <Field label="Échéance" value={extracted.due_date} mono />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Total HT" value={formatXAF(extracted.total_ht || 0)} mono />
                <Field label="TVA" value={formatXAF(extracted.total_vat || 0)} mono />
                <Field label="Total TTC" value={formatXAF(extracted.total_ttc || 0)} mono highlight />
              </div>
              {extracted.lines?.length > 0 && (
                <div>
                  <div className="overline mb-2">Lignes ({extracted.lines.length})</div>
                  <div className="bg-obsidian-900 rounded-sm max-h-48 overflow-y-auto">
                    {extracted.lines.map((l, i) => (
                      <div key={i} className="px-3 py-2 border-b border-line/50 last:border-b-0 flex items-center gap-2 text-xs">
                        <span className="flex-1 truncate">{l.description}</span>
                        <span className="font-mono text-obsidian-400">{l.qty} × {formatXAF(l.unit_price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {extracted.confidence !== undefined && (
                <div className="overline">Confiance OCR : <span className={extracted.confidence > 0.7 ? "text-ok" : "text-warn"}>{Math.round((extracted.confidence || 0) * 100)}%</span></div>
              )}

              {/* Inject */}
              <div className="pt-4 border-t border-line space-y-3">
                <div>
                  <label className="label block">Entité cible</label>
                  <select className="input" value={targetEntity} onChange={(e) => setTargetEntity(e.target.value)}>
                    {opEntities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => ingest("purchase")} disabled={ingesting} className="btn-secondary" data-testid="ocr-to-purchase">
                    <ShoppingCart size={14} /> Vers Achats
                  </button>
                  <button onClick={() => ingest("accounting")} disabled={ingesting} className="btn-primary" data-testid="ocr-to-accounting">
                    <BookOpen size={14} /> Vers Comptabilité
                  </button>
                </div>

                {result && (
                  <div className="p-3 bg-ok/10 border border-ok/30 rounded-sm text-xs">
                    <div className="flex items-center gap-2 text-ok mb-1"><CheckCircle2 size={14} /> Injection réussie ({result.target})</div>
                    <div className="text-obsidian-300">
                      {result.target === "purchase" && result.data.created && <>Bon de commande créé : {result.data.created.po_number} · {formatXAF(result.data.created.total)}</>}
                      {result.target === "accounting" && <>{result.data.count} écriture(s) OHADA créée(s)</>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Field({ label, value, mono, highlight }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${highlight ? "text-citron font-bold text-lg" : "text-obsidian-100"}`}>{value || "—"}</div>
    </div>
  );
}
